import { EventContextProvider } from "@codat/analytics";
import {
  AuthFlowIcon,
  BookOpenTextIcon,
  BuildingIcon,
  Col,
  Grid,
  PowerCordPlugIcon,
  RocketIcon,
  Row,
  SettingsIcon,
  WebhooksIcon,
} from "@codat/orchard-ui";
import { useFeatureStatus } from "portal-ui-remote/FeatureStatusContext";
import React from "react";

import { paths } from "components/App/paths";
import Page from "components/Page";
import PageHeader from "components/PageHeader";

import SettingsSection from "./SettingsSection";
import { useSettingsOverviewNavItems } from "./useSettingsOverviewNavItems";

const SettingsOverview = () => {
  const navItems = useSettingsOverviewNavItems();
  const { featureStatus } = useFeatureStatus();
  const newWebhooks = featureStatus?.["Rules.UseNewWebhooks"];

  return (
    <Page>
      <EventContextProvider name="Settings Overview">
        <Grid>
          <PageHeader header="Settings" />
          <Row>
            {navItems.products.length > 0 && (
              <Col size={6}>
                <EventContextProvider name="Products section">
                  <SettingsSection
                    data-testid="product-settings-section"
                    title="Products"
                    icon={RocketIcon}
                    iconColor="warning"
                    navItems={navItems.products}
                    headerLink={{
                      name: "Manage products",
                      path: paths.settings.MANAGE_PRODUCTS,
                      icon: SettingsIcon,
                      linkText: "Manage products",
                    }}
                  />
                </EventContextProvider>
              </Col>
            )}
            {navItems.integrations.length > 0 && (
              <Col size={6}>
                <EventContextProvider name="Integrations section">
                  <SettingsSection
                    data-testid="integrations-section"
                    title="Integrations"
                    icon={PowerCordPlugIcon}
                    iconColor="action"
                    headerButton={{
                      buttonName: "Integration Docs",
                      href: "https://docs.codat.io/integrations/all-integrations",
                      icon: BookOpenTextIcon,
                      label: "Integration docs",
                    }}
                    navItems={navItems.integrations}
                  />
                </EventContextProvider>
              </Col>
            )}
          </Row>
          <Row>
            {navItems.authFlow.length > 0 && (
              <Col size={12}>
                <EventContextProvider name="Auth Flow section">
                  <SettingsSection
                    data-testid="auth-flow-section"
                    title="Auth flow"
                    icon={AuthFlowIcon}
                    iconColor="positive"
                    headerButton={{
                      buttonName: "Auth Flow Docs",
                      href: "https://docs.codat.io/auth-flow/overview",
                      icon: BookOpenTextIcon,
                      label: "Auth flow docs",
                    }}
                    navItems={navItems.authFlow}
                  />
                </EventContextProvider>
              </Col>
            )}
            {navItems.webhooks.length > 0 && (
              <Col size={12}>
                <EventContextProvider name="Webhooks section">
                  <SettingsSection
                    data-testid="webhooks-section"
                    title="Webhooks"
                    icon={WebhooksIcon}
                    iconColor="neutral"
                    navItems={navItems.webhooks}
                    headerButton={{
                      buttonName: "Webhook Docs",
                      href: newWebhooks
                        ? "https://docs.codat.io/using-the-api/webhooks/overview"
                        : "https://docs.codat.io/using-the-api/webhooks/legacy/core-rules-create",
                      icon: BookOpenTextIcon,
                      label: "Webhook docs",
                    }}
                  />
                </EventContextProvider>
              </Col>
            )}
          </Row>
          {navItems.organization.length > 0 && (
            <Row>
              <Col size={12}>
                <EventContextProvider name="Organization section">
                  <SettingsSection
                    data-testid="organization-settings-section"
                    title="Organization settings"
                    icon={BuildingIcon}
                    iconColor="interface"
                    navItems={navItems.organization}
                  />
                </EventContextProvider>
              </Col>
            </Row>
          )}
        </Grid>
      </EventContextProvider>
    </Page>
  );
};

export default SettingsOverview;
